import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { WorkflowBranch, WorkflowNode } from '../../../types/backend';
import { evidenceKeys, toolNames } from '../utils/workflowDraft';
import { grantLabel } from '../../workflow/utils/toolGrants';

interface Props {
  node: WorkflowNode;
  stageIndex: number;
  stageCount: number;
  /** The other steps in this stage: the only ones a step may wait on. */
  siblings: WorkflowNode[];
  /** Results earlier steps are guaranteed to have written down by the time this one runs. */
  availableResults: string[];
  toolOptions: string[];
  onChange: (patch: Partial<WorkflowNode>) => void;
  onAddRoute: () => void;
  onRouteChange: (routeIndex: number, patch: Partial<WorkflowBranch>) => void;
  onRemoveRoute: (routeIndex: number) => void;
}

// A heading for a run of fields that answer one question about the step. The form was a flat
// stack of eight controls with a line of prose under each explaining itself; the prose is gone
// and these are what carry the reading order instead.
const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Divider textAlign="left" sx={{ mb: 1.25 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.disabled', letterSpacing: 0.8, textTransform: 'uppercase' }}
      >
        {title}
      </Typography>
    </Divider>
    <Stack spacing={1.25}>{children}</Stack>
  </Box>
);

// The form for one step. Every control maps to one WorkflowNode field; the rules that cut across
// steps live in utils/workflowDraft and refuse there.
const WorkflowStepEditor: React.FC<Props> = ({
  node,
  stageIndex,
  stageCount,
  siblings,
  availableResults,
  toolOptions,
  onChange,
  onAddRoute,
  onRouteChange,
  onRemoveRoute,
}) => {
  const isDecision = node.kind === 'decision';
  const later = Array.from({ length: stageCount }, (_, i) => i + 1).filter(
    (l) => l > stageIndex + 1,
  );
  const siblingIds = siblings.map((s) => s.id).filter(Boolean);
  const siblingLabel = (id: string) => siblings.find((s) => s.id === id)?.label || id;

  return (
    <Stack spacing={2} sx={{ mt: 1.5 }}>
      <Stack spacing={1.25}>
        <TextField
          size="small"
          label="Step name"
          value={node.label}
          onChange={(e) => onChange({ label: e.target.value })}
        />
        <Stack direction="row" spacing={1}>
          <TextField
            select
            size="small"
            label="This is a"
            value={node.kind ?? 'step'}
            onChange={(e) =>
              onChange({ kind: e.target.value === 'decision' ? 'decision' : 'step' })
            }
            sx={{ flex: 1 }}
          >
            <MenuItem value="step">Step</MenuItem>
            <MenuItem value="decision">Decision</MenuItem>
          </TextField>
          <TextField
            size="small"
            label="Key"
            value={node.id}
            onChange={(e) => onChange({ id: e.target.value })}
            sx={{ flex: 1.4 }}
          />
        </Stack>
        <TextField
          size="small"
          label="Goal"
          multiline
          minRows={6}
          value={node.goal}
          onChange={(e) => onChange({ goal: e.target.value })}
        />
      </Stack>

      <Group title="When it runs">
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={node.gate ?? false}
              onChange={(e) => onChange({ gate: e.target.checked })}
            />
          }
          label={<Typography variant="body2">Needs approval first</Typography>}
          sx={{ ml: 0 }}
        />
        {!isDecision && (
          <Autocomplete
            multiple
            size="small"
            options={siblingIds.filter((id) => id !== node.id)}
            value={node.depends_on ?? []}
            getOptionLabel={siblingLabel}
            onChange={(_, value) => onChange({ depends_on: value })}
            noOptionsText="Nothing else in this stage"
            renderInput={(params) => <TextField {...params} label="Runs after" />}
          />
        )}
      </Group>

      <Group title="What it may change">
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={toolOptions}
          value={node.allowed_tools ?? []}
          onChange={(_, value) => onChange({ allowed_tools: toolNames(value) })}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box component="li" key={key} {...optionProps}>
                <Box>
                  <Typography variant="body2">{grantLabel(option)}</Typography>
                  {grantLabel(option) !== option && (
                    <Typography variant="caption" color="text.secondary">
                      {option}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((tool, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip key={key} size="small" label={grantLabel(tool)} title={tool} {...tagProps} />
              );
            })
          }
          renderInput={(params) => <TextField {...params} label="Tools" />}
        />
      </Group>

      {/* A decision reads but never records: it routes on the facts a step established, and what
          it writes down is the branch it took and why. */}
      <Group title="Results">
        <Autocomplete
          multiple
          size="small"
          options={availableResults}
          value={node.inputs ?? []}
          onChange={(_, value) => onChange({ inputs: value })}
          noOptionsText="No earlier step writes anything down yet"
          renderInput={(params) => <TextField {...params} label="Reads" />}
        />
        {!isDecision && (
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={[]}
            value={node.evidence_required ?? []}
            onChange={(_, value) => onChange({ evidence_required: evidenceKeys(value) })}
            renderInput={(params) => <TextField {...params} label="Records" />}
          />
        )}
      </Group>

      {isDecision && (
        <Group title="Routes">
          {(node.branches ?? []).map((route, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                label="If"
                value={route.condition}
                onChange={(e) => onRouteChange(i, { condition: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                select
                size="small"
                label="Go to"
                value={later.includes(route.target_layer) ? route.target_layer : ''}
                onChange={(e) => onRouteChange(i, { target_layer: Number(e.target.value) })}
                sx={{ width: 110 }}
              >
                {later.map((layer) => (
                  <MenuItem key={layer} value={layer}>
                    Stage {layer}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton
                size="small"
                aria-label={`Remove route ${i + 1}`}
                onClick={() => onRemoveRoute(i)}
                sx={{ mt: 0.5 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddRoute}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add route
          </Button>
        </Group>
      )}
    </Stack>
  );
};

export default WorkflowStepEditor;
